/**
 * Resolução de fuso horário com fallback — sem jamais usar o fuso do aparelho.
 *
 * O problema: `tripPhase` depende de saber que dia é hoje no fuso da VIAGEM.
 * Em Node isso é trivial. Em Hermes/Android, o suporte a fusos IANA depende do
 * ICU embarcado no binário, e o modo de falha perigoso não é a exceção — é o
 * runtime que aceita um fuso desconhecido e **devolve UTC em silêncio**.
 * Nesse caso a Home trocaria de fase algumas horas cedo ou tarde, sem erro
 * nenhum aparecer.
 *
 * Estratégia, em ordem:
 *   1. `Intl`, mas só depois de PROVAR que ele honra o fuso (não basta não lançar);
 *   2. tabela de transições de offset UTC enviada pelo servidor para a janela da
 *      viagem — o servidor sempre tem ICU completo, e o cliente faz aritmética pura;
 *   3. erro alto.
 *
 * O fuso do dispositivo nunca entra na conta, em nenhum dos três caminhos.
 */

import { type CalendarDate, calendarDate } from './calendar.js';

const MS_PER_MINUTE = 60_000;

/**
 * Uma transição de offset: a partir de `from` (epoch ms), o fuso está a
 * `offsetMinutes` do UTC. Ex.: -240 para horário de verão de Nova York.
 *
 * O servidor gera isto para a janela da viagem. Uma viagem de poucas semanas
 * tem no máximo uma transição, então o payload é minúsculo.
 */
export interface UtcOffsetTransition {
  readonly from: number;
  readonly offsetMinutes: number;
}

/** Transições em ordem crescente de `from`. */
export type UtcOffsetSchedule = readonly UtcOffsetTransition[];

export class TimeZoneUnavailableError extends Error {
  constructor(timeZone: string) {
    super(
      `Não foi possível resolver o fuso "${timeZone}": este runtime não tem ` +
        'dados IANA confiáveis e nenhuma tabela de offsets foi fornecida. ' +
        'Falhar aqui é preferível a assumir o fuso do aparelho.',
    );
    this.name = 'TimeZoneUnavailableError';
  }
}

/**
 * Instante e offset de referência para provar que o runtime conhece um fuso.
 *
 * 15/01/2026 12:00Z é inverno no hemisfério norte e verão no sul, então os
 * offsets abaixo são estáveis e não dependem de regra de horário de verão que
 * possa mudar. Um runtime que devolve UTC em silêncio falha esta checagem.
 */
const PROBES: Record<string, number> = {
  'America/New_York': -300,
  'America/Sao_Paulo': -180,
  'Europe/Lisbon': 0,
  'Asia/Tokyo': 540,
};

const PROBE_INSTANT = Date.UTC(2026, 0, 15, 12, 0, 0);

/**
 * Offset de um fuso num instante, via `Intl`. Devolve `null` se o runtime não
 * souber responder.
 */
function intlOffsetMinutes(timeZone: string, instant: number): number | null {
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).formatToParts(new Date(instant));
  } catch {
    return null;
  }

  const read = (type: Intl.DateTimeFormatPartTypes): number | null => {
    const part = parts.find((p) => p.type === type);
    return part ? Number(part.value) : null;
  };

  const year = read('year');
  const month = read('month');
  const day = read('day');
  const hour = read('hour');
  const minute = read('minute');
  const second = read('second');

  if (
    year === null || month === null || day === null ||
    hour === null || minute === null || second === null
  ) {
    return null;
  }

  const asUtc = Date.UTC(year, month - 1, day, hour, minute, second);
  return Math.round((asUtc - instant) / MS_PER_MINUTE);
}

const supportCache = new Map<string, boolean>();

/**
 * O runtime conhece este fuso *de verdade*?
 *
 * Para fusos com offset conhecido, compara com o esperado. Para os demais,
 * exige apenas que `Intl` responda de forma coerente — e trata "exatamente UTC
 * num fuso que não é UTC" como suspeito, que é a assinatura do ICU reduzido.
 */
export function hasReliableTimeZoneSupport(timeZone: string): boolean {
  const cached = supportCache.get(timeZone);
  if (cached !== undefined) return cached;

  let supported: boolean;
  const observed = intlOffsetMinutes(timeZone, PROBE_INSTANT);

  if (observed === null) {
    supported = false;
  } else {
    const expected = PROBES[timeZone];
    if (expected !== undefined) {
      supported = observed === expected;
    } else if (timeZone === 'UTC' || timeZone === 'Etc/UTC') {
      supported = observed === 0;
    } else {
      // Sem sonda específica: confia, mas confirma que o runtime distingue
      // fusos — se um fuso conhecido também falhar, o ICU é reduzido.
      supported = intlOffsetMinutes('America/New_York', PROBE_INSTANT) === -300;
    }
  }

  supportCache.set(timeZone, supported);
  return supported;
}

/** Apenas para testes: limpa o cache de detecção. */
export function resetTimeZoneSupportCache(): void {
  supportCache.clear();
}

/** Offset vigente segundo a tabela enviada pelo servidor. */
export function offsetFromSchedule(
  schedule: UtcOffsetSchedule,
  instant: number,
): number | null {
  let current: number | null = null;
  for (const transition of schedule) {
    if (transition.from <= instant) current = transition.offsetMinutes;
    else break;
  }
  return current;
}

/** Data civil de um instante, dado o offset em minutos. */
export function civilDateAtOffset(instant: number, offsetMinutes: number): CalendarDate {
  const shifted = new Date(instant + offsetMinutes * MS_PER_MINUTE);
  const year = String(shifted.getUTCFullYear()).padStart(4, '0');
  const month = String(shifted.getUTCMonth() + 1).padStart(2, '0');
  const day = String(shifted.getUTCDate()).padStart(2, '0');
  return calendarDate(`${year}-${month}-${day}`);
}

/**
 * Que dia é hoje no fuso pedido.
 *
 * `schedule` é o fallback: quando o runtime não é confiável, usa-se a tabela de
 * offsets do servidor. Sem ela e sem `Intl` confiável, lança —
 * `TimeZoneUnavailableError` é um resultado melhor que uma fase errada.
 */
export function resolveToday(
  timeZone: string,
  now: Date = new Date(),
  schedule?: UtcOffsetSchedule,
): CalendarDate {
  const instant = now.getTime();

  if (hasReliableTimeZoneSupport(timeZone)) {
    const offset = intlOffsetMinutes(timeZone, instant);
    if (offset !== null) return civilDateAtOffset(instant, offset);
  }

  if (schedule && schedule.length > 0) {
    const offset = offsetFromSchedule(schedule, instant);
    if (offset !== null) return civilDateAtOffset(instant, offset);
  }

  throw new TimeZoneUnavailableError(timeZone);
}

/**
 * Instante (epoch ms) de uma hora civil `HH:MM` numa data civil, no fuso
 * pedido. É o inverso de `resolveToday`: serve para agendar ("avisar 30 min
 * antes das 10:30 do dia 12, no fuso da viagem").
 *
 * Numa transição de horário de verão a hora pode não existir ou existir
 * duas vezes; aqui vale a primeira leitura consistente — para um aviso de
 * atividade, minutos de diferença num dia por ano não mudam a decisão.
 */
export function localToInstant(timeZone: string, date: CalendarDate, time: string): number {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const [hh, mm] = time.split(':').map(Number) as [number, number];
  const comoUtc = Date.UTC(y, m - 1, d, hh, mm, 0, 0);
  const primeiro = intlOffsetMinutes(timeZone, comoUtc);
  if (primeiro === null) throw new TimeZoneUnavailableError(timeZone);
  const tentativa = comoUtc - primeiro * 60_000;
  const segundo = intlOffsetMinutes(timeZone, tentativa);
  if (segundo === null || segundo === primeiro) return tentativa;
  return comoUtc - segundo * 60_000;
}
